// externals
import type { Reducer } from 'redux';
import type { Draft } from 'immer';
import { produce } from 'immer';
import { nanoid } from 'nanoid';

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { DXG_DMS_EXPORT_STATE_BAGGAGE_NAME, type PushToDmsState } from '@makemydeal/dr-dash-types';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';

// consts/enums
import {
    PUSH_TO_DMS,
    PUSH_TO_DMS_SUCCESS,
    PUSH_TO_DMS_ERROR,
    PUSH_TO_DMS_PLUS,
    PUSH_TO_DMS_WS_EVENT,
    PUSH_TO_CDMS_RESET_ERROR,
    PUSH_TO_DMS_RESPONSE
} from '../actionTypes/pushToDmsActionTypes';
import { COX_DMS_PLUS, PUSH_TO_DMS_FLOW } from '../constants';
import { OFFER_SAVE_SUCCESS, OFFER_SAVE_FAILURE } from '../actionTypes/offerActionTypes';

export const pushToDmsInitialState: PushToDmsState = {
    transferToDMS: '',
    responseStatusCode: 500,
    pushToDmsWsEvent: {} as PushToDmsState['pushToDmsWsEvent']
};

export type PushToDmsReducer = Reducer<PushToDmsState>;

export const reducer: PushToDmsReducer = (state: PushToDmsState = pushToDmsInitialState, action: AnyFSA) => {
    return produce(state, (draft: Draft<PushToDmsState>) => {
        switch (action?.type) {
            case PUSH_TO_DMS: {
                const lastSucceedDmsDealId = draft.lastSucceedDmsDealId;
                Object.assign(draft, pushToDmsInitialState);
                draft.lastSucceedDmsDealId = lastSucceedDmsDealId;
                draft.correlationId = nanoid();
                // Object.assign won't clear keys absent from initial state; reset the dedup marker
                // so a stale postback from the previous push can't suppress this push's WS fallback.
                draft.postBackResponse = undefined;
                break;
            }

            case OFFER_SAVE_FAILURE:
            case PUSH_TO_DMS_ERROR:
                draft.error = action.payload
                    ? { errorDetails: action.payload.errorDetails, statusCode: action.payload.statusCode }
                    : { statusCode: 500 };
                break;

            case PUSH_TO_DMS_PLUS: {
                draft.transferToDMS = COX_DMS_PLUS;
                break;
            }

            case OFFER_SAVE_SUCCESS: {
                const offerSaveSource = action.meta.source;
                if (offerSaveSource === PUSH_TO_DMS_FLOW) {
                    const { dealXgId, dealXgVersion } = action.payload;
                    draft.dealXgId = dealXgId;
                    draft.dealXgVersion = dealXgVersion;
                }
                break;
            }

            case PUSH_TO_DMS_SUCCESS:
                draft.responseStatusCode = action.payload.data.status_code;
                draft.cdmsResponseData = action.payload.data.message;
                draft.cdmsStorefrontErrorMessage = action.payload.data.response?.message?.error;
                if (!draft.cdmsStorefrontErrorMessage && !action.payload.ok)
                    draft.cdmsStorefrontErrorMessage = action.payload.data.message;
                break;

            case PUSH_TO_DMS_WS_EVENT: {
                const wsData = action.payload?.data;
                const dmsDealId = wsData?.payload?.dms_deal_id;

                draft.pushToDmsWsEvent = wsData || {};

                // Ignore the WS event for the badge if the postback already handled this push.
                if (wsData && !draft.postBackResponse) {
                    draft.dmsExportState = {
                        exportBy: wsData.exportBy,
                        exportedAt: wsData.eventTime,
                        system: wsData.payload?.dms_name,
                        status: dmsDealId ? 'success' : 'failure',
                        identifier: dmsDealId || undefined
                    };
                }

                if (dmsDealId) {
                    draft.lastSucceedDmsDealId = dmsDealId;
                    draft.lastSucceedDmsTime = new Date();
                }
                break;
            }
            case PUSH_TO_DMS_RESPONSE: {
                const response = action.payload;
                if (response) {
                    const dmsDealId = response.dmsDealId;

                    draft.postBackResponse = response;
                    draft.lastSucceedDmsDealId = dmsDealId;
                    draft.lastSucceedDmsTime = new Date();
                    if (!dmsDealId) {
                        draft.cdmsResponseData = response;
                        draft.cdmsStorefrontErrorMessage = response.message;
                    }

                    draft.dmsExportState = {
                        exportBy: action.meta?.exportBy || '',
                        exportedAt: new Date().toISOString(),
                        system: response.dmsName as string,
                        status: dmsDealId ? 'success' : 'failure',
                        identifier: dmsDealId || undefined
                    };
                }
                break;
            }
            case PUSH_TO_CDMS_RESET_ERROR:
                draft.cdmsStorefrontErrorMessage = '';
                draft.cdmsResponseData = undefined;
                break;

            case bootstrapActionTypes.BOOTSTRAP_SUCCESS:
                draft.dmsExportState = action.payload?.baggage?.find?.(
                    (b: any) => b.name === DXG_DMS_EXPORT_STATE_BAGGAGE_NAME
                )?.value?.content;
                break;

            default:
                break;
        }
    });
};
